import { useState, type ReactNode } from 'react'
import { Check, Copy, FileText } from 'lucide-react'

import { isRepositoryReference } from './guides'
import { plainText, type Block, type Inline } from './parse'

/**
 * The rendering half of the guide reader.
 *
 * Every node becomes a React element — there is no `dangerouslySetInnerHTML`
 * anywhere in this file, and the parser never produces raw HTML, so a guide is
 * rendered as text by construction rather than by sanitising it afterwards.
 */

function Inlines({ nodes }: { nodes: readonly Inline[] }): ReactNode {
  return nodes.map((node, index) => {
    switch (node.kind) {
      case 'text':
        return <span key={index}>{node.value}</span>
      case 'code':
        return <code key={index} className="md-code">{node.value}</code>
      case 'strong':
        return <strong key={index}><Inlines nodes={node.children} /></strong>
      case 'em':
        return <em key={index}><Inlines nodes={node.children} /></em>
      case 'link': {
        const label = <Inlines nodes={node.children} />
        if (isRepositoryReference(node.href)) {
          return (
            <span key={index} className="md-reference" title={`${node.href} — in the DDWS docs repository`}>
              <FileText size={13} strokeWidth={1.8} aria-hidden="true" />
              {label}
            </span>
          )
        }
        const external = /^https?:/.test(node.href)
        return (
          <a
            key={index}
            className="md-link"
            href={node.href}
            {...(external ? { target: '_blank', rel: 'noreferrer noopener' } : {})}
          >
            {label}
          </a>
        )
      }
    }
  })
}

function CodeBlockView({ language, code }: { language: string; code: string }) {
  const [copied, setCopied] = useState(false)
  const copy = () => {
    // Clipboard access is permissioned and absent in insecure contexts; the
    // block is still readable and selectable, so a refusal is not an error to
    // report — the button simply does not confirm.
    void navigator.clipboard?.writeText(code).then(
      () => { setCopied(true); window.setTimeout(() => setCopied(false), 1600) },
      () => undefined,
    )
  }
  return (
    <div className="md-codeblock">
      <div className="md-codeblock-bar">
        <span className="md-codeblock-language">{language || 'text'}</span>
        <button type="button" className="md-copy" onClick={copy} aria-label={copied ? 'Copied' : 'Copy code'}>
          {copied ? <Check size={14} strokeWidth={2} aria-hidden="true" /> : <Copy size={14} strokeWidth={1.8} aria-hidden="true" />}
          <span>{copied ? 'Copied' : 'Copy'}</span>
        </button>
      </div>
      <pre><code>{code}</code></pre>
    </div>
  )
}

function Heading({ level, id, children }: { level: number; id: string; children: ReactNode }) {
  // The guide's own h1 is dropped by `Markdown` — the console's page header is
  // the page's one top-level heading — so section levels carry through as they
  // were written and the document keeps an unbroken h1 → h2 → h3 outline.
  const Tag = (`h${Math.min(level, 6)}`) as 'h2'
  return (
    <Tag id={id} className="md-heading">
      <a className="md-anchor" href={`#${id}`} aria-label={`Link to this section`}>#</a>
      {children}
    </Tag>
  )
}

function BlockView({ block }: { block: Block }): ReactNode {
  switch (block.kind) {
    case 'heading':
      return <Heading level={block.level} id={block.id}><Inlines nodes={block.children} /></Heading>
    case 'paragraph':
      return <p className="md-paragraph"><Inlines nodes={block.children} /></p>
    case 'code':
      return <CodeBlockView language={block.language} code={block.code} />
    case 'rule':
      return <hr className="md-rule" />
    case 'quote':
      return <blockquote className="md-quote"><Blocks blocks={block.blocks} /></blockquote>
    case 'list': {
      const Tag = block.ordered ? 'ol' : 'ul'
      return (
        <Tag className="md-list">
          {block.items.map((item, index) => (
            <li key={index}>
              <Inlines nodes={item.children} />
              {item.sub.length > 0 && (
                <ul className="md-list">
                  {item.sub.map((sub, subIndex) => <li key={subIndex}><Inlines nodes={sub.children} /></li>)}
                </ul>
              )}
            </li>
          ))}
        </Tag>
      )
    }
    case 'table':
      return (
        <div className="md-table-scroll">
          <table className="md-table">
            <thead>
              <tr>{block.head.map((cell, index) => (
                // A comparison table opens with an empty corner cell. A header
                // with nothing in it is a header with no accessible name, so it
                // stays an ordinary cell.
                plainText(cell) === ''
                  ? <td key={index} className="md-table-corner" />
                  : <th key={index} scope="col"><Inlines nodes={cell} /></th>
              ))}</tr>
            </thead>
            <tbody>
              {block.rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, cellIndex) => (
                    cellIndex === 0
                      ? <th key={cellIndex} scope="row"><Inlines nodes={cell} /></th>
                      : <td key={cellIndex}><Inlines nodes={cell} /></td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )
  }
}

function Blocks({ blocks }: { blocks: readonly Block[] }): ReactNode {
  return blocks.map((block, index) => <BlockView key={index} block={block} />)
}

/** Renders a parsed guide, minus its own title — the page header carries that. */
export function Markdown({ blocks }: { blocks: readonly Block[] }) {
  const body = blocks.filter((block) => !(block.kind === 'heading' && block.level === 1))
  return <div className="md-body"><Blocks blocks={body} /></div>
}
